'use client';

import { useState, useEffect } from 'react';

const SHORTCUTS = [
  { keys: ['?'], desc: 'Toggle this help overlay' },
  { keys: ['F'], desc: 'Toggle fullscreen mode' },
  { keys: ['R'], desc: 'Reset configuration to defaults' },
  { keys: ['D'], desc: 'Toggle dark mode' },
  { keys: ['←', '→'], desc: 'Navigate between variants' },
  { keys: ['S'], desc: 'Share configuration link' },
  { keys: ['Ctrl', 'Shift', 'H'], desc: 'Toggle hotspot overlay' },
  { keys: ['Esc'], desc: 'Close overlays / panels' },
  { keys: ['Tab'], desc: 'Cycle through options' },
];

export function ShortcutHelp() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        const tag = (e.target as HTMLElement).tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative bg-white dark:bg-zinc-900 rounded-xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-sm mx-4 overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-800 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Keyboard Shortcuts</h2>
          <button
            onClick={() => setOpen(false)}
            className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 text-lg leading-none"
          >
            &times;
          </button>
        </div>
        <div className="px-4 py-2 divide-y divide-zinc-100 dark:divide-zinc-800">
          {SHORTCUTS.map((s, i) => (
            <div key={i} className="flex items-center justify-between py-2">
              <span className="text-sm text-zinc-600 dark:text-zinc-400">{s.desc}</span>
              <div className="flex items-center gap-1">
                {s.keys.map((k, ki) => (
                  <kbd
                    key={ki}
                    className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-[11px] font-mono font-medium text-zinc-600 dark:text-zinc-300"
                  >
                    {k}
                  </kbd>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="px-4 py-2.5 bg-zinc-50 dark:bg-zinc-950 border-t border-zinc-100 dark:border-zinc-800">
          <p className="text-[10px] text-zinc-400 text-center">
            Press <kbd className="px-1 py-0.5 rounded bg-zinc-200 dark:bg-zinc-800 text-[10px] font-mono">?</kbd> to close
          </p>
        </div>
      </div>
    </div>
  );
}
